import { StructuredQuestion } from '../interfaces/questions'

export interface NumberingChange {
  from: string
  to: string
  questionText: string
}

/**
 * Compare current question numbers against what renumberSequentially would produce.
 * Returns the list of changes — empty means no inconsistencies.
 */
export function getNumberingChanges(
  questions: StructuredQuestion[],
): NumberingChange[] {
  const renumbered = renumberSequentially(questions)
  return questions
    .map((q, i) => ({
      from: q.question_number,
      to: renumbered[i].question_number,
      questionText: q.question,
    }))
    .filter((c) => c.from !== c.to)
}

/**
 * Renumber questions sequentially, preserving sub-letter suffixes.
 *
 * Integer-numbered questions (e.g. "1", "2", "3") are assigned new sequential
 * integers in list order. Sub-lettered questions (e.g. "2a", "2b") keep their
 * suffix but have their base updated to match the integer question's new number.
 *
 * Pass deletedNumbers to clear conditional_on / depends_on refs that pointed to
 * a question that has already been removed from the list.
 *
 * Examples (insert new "" after "2"):
 *   ["1","2","2a","","3","4","10a"] → ["1","2","2a","3","4","5","10a"]
 *   (base "3"→"4" propagates to "3a"→"4a" if present; "10a" has no parent so stays)
 */
export function renumberSequentially(
  questions: StructuredQuestion[],
  deletedNumbers: Set<string> = new Set(),
): StructuredQuestion[] {
  // Detect orphaned sub-questions: a sub is orphaned if the question immediately
  // before it does not share the same base (i.e. it's been dragged away from its
  // parent group). Orphaned subs are converted to integers.
  const orphanedIndices = new Set<number>()
  for (let i = 0; i < questions.length; i++) {
    const suffix = questions[i].question_number.match(/[a-z]$/i)?.[0] ?? ''
    if (!suffix) continue
    const base = questions[i].question_number.replace(/[a-z]$/i, '')
    let adjacentToGroup = false
    if (i > 0 && !orphanedIndices.has(i - 1)) {
      const prevBase = questions[i - 1].question_number.replace(/[a-z]$/i, '')
      if (prevBase === base) adjacentToGroup = true
    }
    if (!adjacentToGroup) orphanedIndices.add(i)
  }

  const mapping = new Map<string, string>()
  let counter = 0

  // First pass: assign sequential integers to integer-only questions + orphaned subs
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i]
    const suffix = q.question_number.match(/[a-z]$/i)?.[0] ?? ''
    if (!suffix || orphanedIndices.has(i)) {
      counter++
      const newNum = String(counter)
      if (q.question_number !== newNum) {
        mapping.set(q.question_number, newNum)
      }
    }
  }

  // Second pass: reassign sub-question letters sequentially within each group.
  // This handles both base updates (when the parent integer shifted) and
  // letter reordering (when subs were dragged within a group).
  let currentBase = ''
  let letterCounter = 0

  for (let i = 0; i < questions.length; i++) {
    if (orphanedIndices.has(i)) {
      currentBase = ''
      letterCounter = 0
      continue
    }

    const q = questions[i]
    const suffix = q.question_number.match(/[a-z]$/i)?.[0] ?? ''
    const base = q.question_number.replace(/[a-z]$/i, '')

    if (!suffix) {
      currentBase = base
      letterCounter = 0
      continue
    }

    if (base !== currentBase) {
      currentBase = base
      letterCounter = 0
    }

    const newBase = mapping.get(base) ?? base
    const newLetter = String.fromCharCode('a'.charCodeAt(0) + letterCounter)
    letterCounter++
    const newNum = `${newBase}${newLetter}`
    if (q.question_number !== newNum) {
      mapping.set(q.question_number, newNum)
    }
  }

  // Apply refs update, then remap question_numbers
  const renamed = applyNumberRename(questions, mapping, deletedNumbers)
  return renamed.map((q) => ({
    ...q,
    question_number: mapping.get(q.question_number) ?? q.question_number,
  }))
}

/**
 * Shift sub-question letters down to make room for a new sub at a given letter.
 *
 * Example: shiftSubNumbers(questions, "2", "a") shifts "2a"→"2b", "2b"→"2c", etc.
 * Updates conditional_on / depends_on refs accordingly.
 */
export function shiftSubNumbers(
  questions: StructuredQuestion[],
  base: string,
  fromLetter: string,
): StructuredQuestion[] {
  const mapping = new Map<string, string>()

  // Find all subs of this base at or after fromLetter, process in reverse order
  const subsToShift = questions
    .filter((q) => {
      const match = q.question_number.match(new RegExp(`^${base}([a-z])$`))
      return match !== null && match[1] >= fromLetter
    })
    .map((q) => q.question_number)
    .sort()
    .reverse()

  for (const oldNum of subsToShift) {
    const letter = oldNum.slice(base.length)
    const newLetter = String.fromCharCode(letter.charCodeAt(0) + 1)
    if (newLetter > 'z') return questions // overflow — return unchanged
    mapping.set(oldNum, `${base}${newLetter}`)
  }

  const shifted = applyNumberRename(questions, mapping)
  return shifted.map((q) => ({
    ...q,
    question_number: mapping.get(q.question_number) ?? q.question_number,
  }))
}

/**
 * Compute the next available sub-letter question number for a given reference.
 *
 * Examples:
 *   computeSubNumber("2",  [...])           → "2a"  (no existing subs)
 *   computeSubNumber("2",  [{qn: "2a"}, ]) → "2b"
 *   computeSubNumber("2a", [{qn: "2a"}, ]) → "2b"  (strips trailing letter, base = "2")
 *
 * Returns null if all letters a–z are exhausted.
 */
export function computeSubNumber(
  referenceQNum: string,
  allQuestions: StructuredQuestion[],
): string | null {
  const base = referenceQNum.replace(/[a-z]$/i, '')
  const pattern = new RegExp(`^${base}([a-z])$`)
  const letters = allQuestions
    .map((q) => q.question_number.match(pattern)?.[1])
    .filter((l): l is string => l !== undefined)
    .sort()

  if (letters.length === 0) return `${base}a`

  const next = String.fromCharCode(
    letters[letters.length - 1].charCodeAt(0) + 1,
  )
  if (next > 'z') return null
  return `${base}${next}`
}

/**
 * Apply a batch of question_number renames across the whole list.
 *
 * - mapping: old question_number → new question_number
 * - deletedNumbers: question_numbers that have been removed; any conditional_on
 *   or depends_on references pointing to these are cleared.
 *
 * Used after insert-with-renumber, manual number edit, and delete.
 */
export function applyNumberRename(
  questions: StructuredQuestion[],
  mapping: Map<string, string>,
  deletedNumbers: Set<string> = new Set(),
): StructuredQuestion[] {
  return questions.map((q) => {
    const conditionalNum = q.conditional_on?.question_number

    let conditional_on = q.conditional_on
    if (conditionalNum) {
      if (deletedNumbers.has(conditionalNum)) {
        conditional_on = undefined // clear dangling ref
      } else if (mapping.has(conditionalNum)) {
        conditional_on = {
          ...q.conditional_on!,
          question_number: mapping.get(conditionalNum)!,
        }
      }
    }

    let depends_on = q.depends_on
    if (q.depends_on?.question_numbers) {
      const updated = q.depends_on.question_numbers
        .filter((n) => !deletedNumbers.has(n))
        .map((n) => mapping.get(n) ?? n)
      depends_on = { ...q.depends_on, question_numbers: updated }
    }

    return { ...q, conditional_on, depends_on }
  })
}
